"use client";

import type { MDXComponents } from "mdx/types";
import type { AnchorHTMLAttributes, ReactNode } from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import { GailBlue } from "@/components/common/GailBlue";
import {
  extractBlogMdxSource,
  isPuckInlineEditorValue,
  renderBlogMdxFallback,
} from "@/utils/blogMdxSource";
import { remarkGailBlue } from "@/utils/remarkGailBlue";
import { useMDXComponents } from "../../../mdx-components";

type RuntimeMdxProps = {
  source: string;
  components?: MDXComponents;
  inline?: boolean;
};

type MarkdownTagProps = {
  node?: unknown;
  children?: ReactNode;
} & Record<string, unknown>;

type MarkdownTag = (props: MarkdownTagProps) => ReactNode;

const markdownWrappers = new WeakMap<
  (props: Record<string, unknown>) => ReactNode,
  MarkdownTag
>();

export function normalizeMdxSource(source: unknown): string {
  return extractBlogMdxSource(source);
}

function escapeMdxJsx(source: string): string {
  return source.replace(/[<>]/g, (char) => (char === "<" ? "&lt;" : "&gt;"));
}

function omitMarkdownNode<T extends { node?: unknown }>(props: T): Omit<T, "node"> {
  const rest = { ...props };
  delete rest.node;
  return rest;
}

function SafeMdxLink({
  href,
  children,
  ...props
}: Readonly<
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    node?: unknown;
  }
>) {
  const isExternal = href ? /^https?:\/\//.test(href) : false;

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="text-gail-secondary underline hover:text-black"
      {...omitMarkdownNode(props)}
    >
      {children}
    </a>
  );
}

function withoutMarkdownNode(
  component: (props: Record<string, unknown>) => ReactNode,
): MarkdownTag {
  const existing = markdownWrappers.get(component);
  if (existing) return existing;

  const wrapped: MarkdownTag = (props) => component(omitMarkdownNode(props));
  markdownWrappers.set(component, wrapped);
  return wrapped;
}

function markdownComponents(
  components: MDXComponents,
  inline: boolean,
): Components {
  const mapped: Record<string, MarkdownTag> = {};

  for (const [key, component] of Object.entries(components)) {
    if (typeof component !== "function") continue;
    mapped[key] = withoutMarkdownNode(
      component as (props: Record<string, unknown>) => ReactNode,
    );
  }

  if (inline) {
    mapped.p = function InlineMdxParagraph({ children }) {
      return <>{children}</>;
    };
  }

  return mapped as Components;
}

export default function RuntimeMdx({
  source,
  components,
  inline = false,
}: Readonly<RuntimeMdxProps>) {
  const normalizedSource = normalizeMdxSource(source);
  const mdxComponents = useMDXComponents({
    a: SafeMdxLink,
    GailBlue,
    ...components,
  });

  if (!normalizedSource.trim()) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGailBlue]}
      components={markdownComponents(mdxComponents, inline)}
    >
      {escapeMdxJsx(normalizedSource)}
    </ReactMarkdown>
  );
}

type BlogMdxFieldProps = {
  value: unknown;
  isEditing?: boolean;
  inline?: boolean;
};

/** Renders markdown via RuntimeMdx, or Puck inline editor nodes when editing. */
export function BlogMdxField({
  value,
  isEditing = false,
  inline = false,
}: Readonly<BlogMdxFieldProps>) {
  if (isEditing && isPuckInlineEditorValue(value)) {
    return <>{value}</>;
  }

  const source = extractBlogMdxSource(value);

  if (source.trim()) {
    return <RuntimeMdx source={source} inline={inline} />;
  }

  return <>{renderBlogMdxFallback(value)}</>;
}
